
import React, { ChangeEvent, MouseEvent, useEffect, useRef, useState } from 'react';

import Xarrow from 'react-xarrows/lib';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileImport, faPlus, faSave, faTrash } from '@fortawesome/free-solid-svg-icons';

import {
  ArrowId,
  ArrowModel,
  cancelArrow,
  cloneFunction,
  CodeId,
  CodeModel,
  CodeSheet,
  createCode,
  createCodeSheet,
  createFunction,
  deleteArrow,
  deleteFunction,
  editCode,
  executeFunction,
  FunctionId,
  FunctionModel,
  initArrow,
  linkArrow,
  setFunctionPosition,
} from './utils/model';
import { Function } from './components/Function';


export const App = () => {

  const [ top, setTop ] = useState(0);
  const [ left, setLeft ] = useState(0);

  const [ sheet, setSheet ] = useState(createCodeSheet());

  // load demo sheet
  useEffect(() => {
    const load = async () => {
      const demoSheet: CodeSheet = await fetch('./demoSheet.json').then(res => res.json());
      Object.keys(demoSheet.functionBag).forEach(functionId => executeFunction(demoSheet, functionId as FunctionId));
      setSheet(demoSheet);
    };
    load();
  }, []);

  const fileInput = useRef<HTMLInputElement>(null);

  const handleMouseMove = (e: MouseEvent) => {
    if (!!sheet.partialArrow) {
      setLeft(e.pageX);
      setTop(e.pageY);
    }
  };

  const handleDrag = (functionId: FunctionId, x: number, y: number) => {
    setFunctionPosition(sheet, functionId, x, y);
    setSheet({...sheet});
  }

  const handleOutputClick = (id: FunctionId, outputName: string, e: MouseEvent) => {
    if (!sheet.partialArrow) {
      e.stopPropagation();
      setLeft(e.pageX);
      setTop(e.pageY);
      initArrow(sheet, id, outputName);

      // * we cannot do `setSheet(sheet)` because React will think the object hasn't changed
      // * and will not re-render, react use `Object.is` for comparison
      setSheet({...sheet});
    }
  };

  const handleInputClick = (id: FunctionId, inputName: string) => {
    if (!!sheet.partialArrow) {
      try {
        linkArrow(sheet, id, inputName);
      } catch (error) {
        console.warn(error);
      }

      setSheet({...sheet});
    }
  };

  const handleDeadClick = () => {
    if (!!sheet.partialArrow) {
      cancelArrow(sheet);
      setSheet({...sheet});
    }
  }

  const handleDeleteLink = (id: ArrowId) => {
    deleteArrow(sheet, id);
    setSheet({...sheet});
  };

  const handleCreateFunction = () => {
    createFunction(sheet);
    setSheet({...sheet});
  };

  const handleUpdateFunction = (functionId: FunctionId, codeId: CodeId, newCode: string, multiple: boolean) => {
    try {
      if (multiple) editCode(sheet, codeId, newCode);
      else createCode(sheet, functionId, newCode);
    } catch (error) {
      console.warn(error);
    }

    setSheet({...sheet});
  };

  const handleCloneFunction = (id: FunctionId) => {
    cloneFunction(sheet, id);
    setSheet({...sheet});
  };

  const handleDeleteFunction = (id: FunctionId) => {
    deleteFunction(sheet, id);
    setSheet({...sheet});
  };

  const handleExecuteFunction = (id: FunctionId) => {
    try {
      executeFunction(sheet, id);
    } catch (error) {
      console.warn(error);
    }

    setSheet({...sheet});
  };

  const encodeSheet = () => {
    // remove useless data
    const strippedSheet = JSON.parse(JSON.stringify(sheet));
    delete strippedSheet.partialArrow;
    for (let functionModel of Object.values<FunctionModel>(strippedSheet.functionBag)) {
      for (let input of functionModel.inputs) delete input.value;
      for (let output of functionModel.outputs) delete output.value;
    }

    // encode
    return 'data:text/json;charset=utf-8,' + encodeURIComponent(JSON.stringify(strippedSheet))
  }

  const handleImportClick = () => {
    fileInput.current!.click();
  }

  const handleFileChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files![0];
    if (!file) return;

    const rawData = await file.text();
    const jsonData = JSON.parse(rawData);
    const importedSheet: Partial<CodeSheet> = {
      partialArrow: jsonData.partialArrow,
      functionBag: jsonData.functionBag,
      codeBag: jsonData.codeBag,
      arrowBag: jsonData.arrowBag,
    };

    const emptySheet = createCodeSheet();
    const newSheet = {
      ...emptySheet,
      ...importedSheet,
    }
    
    setSheet(newSheet);
  }

  return (
    <>
      <input type="file" accept=".json" ref={fileInput} className="hidden" onChange={handleFileChange} />

      <div className="fixed top-0 left-0 m-4 z-10 flex flex-col gap-2">
        <button
          onClick={handleCreateFunction}
          className="bg-gray-200 px-2.5 py-1 rounded-full text-gray-800 hover:bg-gray-800 hover:text-gray-200"
        >
          <FontAwesomeIcon icon={faPlus} size="sm"/>
        </button>
        <a
          download="codeSheet.json"
          href={ encodeSheet() }
          className="bg-gray-200 px-2.5 py-1 rounded-full text-gray-800 hover:bg-gray-800 hover:text-gray-200"
        >
          <FontAwesomeIcon icon={faSave} size="sm"/>
        </a>
        <button
          onClick={handleImportClick}
          className="bg-gray-200 px-2.5 py-1 rounded-full text-gray-800 hover:bg-gray-800 hover:text-gray-200"
        >
          <FontAwesomeIcon icon={faFileImport} size="sm"/>
        </button>
      </div>

      <div className="w-full h-full bg-gray-400" onMouseMove={e => handleMouseMove(e)} onClick={handleDeadClick}>

        <div id="mouse-target" className="absolute invisible pointer-events-none" style={{ top, left }}></div>

        {
          Object.values<FunctionModel>(sheet.functionBag).map(fnModel =>
            <Function
              key={fnModel.id}
              model={fnModel}
              code={(sheet.codeBag as Record<string, CodeModel>)[fnModel.codeId].code}
              onDrag={handleDrag}
              onInputClick={handleInputClick}
              onOutputClick={handleOutputClick}
              onCodeUpdate={handleUpdateFunction}
              onClone={handleCloneFunction}
              onDelete={handleDeleteFunction}
              onExecute={handleExecuteFunction}
            />
          )
        }

        {
          Object.values<ArrowModel>(sheet.arrowBag).map(arrow =>
            <Xarrow
              key={arrow.id}
              start={`${arrow.from.id}_output_${arrow.from.name}`}
              end={`${arrow.to.id}_input_${arrow.to.name}`}
              label={{ middle: <button className="rounded-full bg-gray-600 px-1 pb-0.5 leading-none hover:bg-gray-800 text-gray-200 cursor-pointer" onClick={() => handleDeleteLink(arrow.id)}><FontAwesomeIcon icon={faTrash} size="xs" /></button>}}
              startAnchor="right"
              endAnchor="left"
              strokeWidth={1}
              headSize={8}
              color="#1F2937"
            />
          )
        }

        {
          !!sheet.partialArrow ?
            <Xarrow
              key={sheet.partialArrow.id}
              start={`${sheet.partialArrow.from.id}_output_${sheet.partialArrow.from.name}`}
              end='mouse-target'
              startAnchor="right"
              endAnchor="left"
              strokeWidth={1}
              headSize={8}
              color="#FBBF24"
            /> :
            ''
        }

      </div>
    </>
  );
}

export default App;
